import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import Button from './Button';

const Header = ({ onJoinWaitlist, onTryDemo, isMobileDemoVisible = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const [lastY, setLastY] = useState(0);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(null);

  const languages = [
    { code: 'ko', name: '한국어', label: 'KR' },
    { code: 'en', name: 'English', label: 'EN' },
    { code: 'ja', name: '日本語', label: 'JP' },
    { code: 'es', name: 'Español', label: 'ES' },
    { code: 'vi', name: 'Tiếng Việt', label: 'VI' },
    { code: 'fr', name: 'Français', label: 'FR' },
    { code: 'th', name: 'ไทย', label: 'TH' },
    { code: 'ar', name: 'العربية', label: 'AR' },
    { code: 'ru', name: 'Русский', label: 'RU' },
    { code: 'zh', name: '中文', label: 'CN' },
    { code: 'zh-TW', name: '繁體中文', label: 'TW' },
    { code: 'sw', name: 'Kiswahili', label: 'SW' },
    { code: 'pt', name: 'Português', label: 'PT' },
    { code: 'hi', name: 'हिन्दी', label: 'IN' },
    { code: 'id', name: 'Bahasa Indonesia', label: 'ID' },
    { code: 'my', name: 'မြန်မာ', label: 'MM' },
    { code: 'tl', name: 'Filipino', label: 'PH' },
    { code: 'uz', name: 'Uzbek', label: 'UZ' },
    { code: 'ne', name: 'Nepali', label: 'NP' },
    { code: 'it', name: 'Italiano', label: 'IT' },
    { code: 'de', name: 'Deutsch', label: 'DE' },
    { code: 'tr', name: 'Türkçe', label: 'TR' },
    { code: 'el', name: 'Ελληνικά', label: 'GR' },
    { code: 'sv', name: 'Svenska', label: 'SE' },
    { code: 'fi', name: 'Suomi', label: 'FI' },
    { code: 'mn', name: 'Монгол', label: 'MN' },
  ];

  // 언어 감지 및 currentLanguage 업데이트
  useEffect(() => {
    const updateLanguage = () => {
      // 브라우저 언어를 직접 확인 (즉시 사용 가능)
      const browserLang = navigator.language || navigator.languages?.[0] || 'en';
      
      // i18n에서 감지된 언어 (초기화 전에는 browserLang 사용)
      const detectedLang = i18n.language || i18n.resolvedLanguage || browserLang;
      
      // 언어 코드 정규화 (예: 'ko-KR' -> 'ko', 'zh-CN' -> 'zh', 'zh-TW' -> 'zh-TW')
      const normalizedLang = (() => {
        if (detectedLang.startsWith('zh-TW') || detectedLang === 'zh-TW') {
          return 'zh-TW';
        }
        if (detectedLang.startsWith('zh')) {
          return 'zh';
        }
        const baseLang = detectedLang.split('-')[0];
        return baseLang;
      })();

      // languages 배열에서 찾기
      const foundLang = languages.find(lang => lang.code === normalizedLang);
      
      // 찾지 못하면 영어로 폴백
      const selectedLang = foundLang || languages.find(lang => lang.code === 'en') || languages[1];
      
      setCurrentLanguage(selectedLang);
      
      // 디버깅용
      console.log('🔍 언어 감지:', {
        browserLang,
        detectedLang,
        normalizedLang,
        foundLang: foundLang?.code,
        selectedLang: selectedLang?.code,
        i18nLanguage: i18n.language,
        i18nResolvedLanguage: i18n.resolvedLanguage,
        isInitialized: i18n.isInitialized
      });
    };

    // 즉시 실행 (브라우저 언어로 빠르게 설정)
    updateLanguage();

    // i18n이 초기화된 후 다시 업데이트하기 위한 짧은 지연 (캐시된 값이 있으면 적용)
    const timeoutId = setTimeout(() => {
      if (i18n.isInitialized) {
        updateLanguage();
      }
    }, 100);

    // 언어 변경 이벤트 리스너 추가
    const handleLanguageChanged = () => {
      updateLanguage();
    };
    
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      clearTimeout(timeoutId);
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  // 초기 렌더링 시 기본값 설정
  const displayLanguage = currentLanguage || languages.find(lang => lang.code === 'en') || languages[1];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    try {
      sessionStorage.setItem('i18nextLng', langCode);
    } catch {}
    setIsLanguageMenuOpen(false);
  };

  // Close language menu when clicking outside (works for both desktop and mobile menus)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target?.closest?.('[data-lang-menu="true"]')) {
        setIsLanguageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const content = [
      t('header.intro'),
      t('header.demo'),
      t('header.video'),
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY || 0;
      setIsScrolled(currentY > 50);
      // 모바일 프리뷰에서만 숨김 효과 강화: 폭 768 미만에서만 적용
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const goingDown = currentY > lastY;
        // Mobile Demo가 보일 때는 항상 숨김 (역스크롤해도 숨김 유지)
        if (isMobileDemoVisible) {
          setHiddenByScroll(true);
        } else {
        // 내려가면 숨기고, 올리면 보이기
        setHiddenByScroll(goingDown && currentY > 10);
        }
      } else {
        setHiddenByScroll(false);
      }
      setLastY(currentY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastY, isMobileDemoVisible]);

  const handleWaitlistClick = () => {
    onJoinWaitlist?.();
  };

  const handleDemoClick = () => {
    onTryDemo?.();
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[9999] pointer-events-auto transition-all duration-300 transform ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-lg' 
            : 'bg-transparent'
        } ${hiddenByScroll ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              type="button"
              aria-label="Go to hero"
              className="flex-shrink-0"
              onClick={(e) => {
                e.preventDefault();
                const hero = document.querySelector('.hero-section');
                if (hero) {
                  hero.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <img
                src={isScrolled ? "/logl-logo.png" : "/logl-logo-white.png"}
                alt="logl logo"
                className="h-14 w-auto transition-opacity duration-300 cursor-pointer"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a
                  href="#features"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`${isScrolled ? 'text-gray-600' : 'text-orange-100'} hover:text-orange-500 transition-colors duration-200 text-sm font-medium cursor-pointer`}
                >
                  {content[0]}
              </a>
              <button
                onClick={handleDemoClick}
                className={`${isScrolled ? 'text-gray-600' : 'text-orange-100'} hover:text-orange-500 transition-colors duration-200 text-sm font-medium`}
              >
                {content[1]}
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  const videoSection = document.getElementById('video');
                  if (videoSection) {
                    videoSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className={`${isScrolled ? 'text-gray-600' : 'text-orange-100'} hover:text-orange-500 transition-colors duration-200 text-sm font-medium`}
              >
                {content[2]}
              </button>

              {/* Language Selector */}
              <div className="relative" data-lang-menu="true">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className={`flex items-center space-x-2 ${isScrolled ? 'text-gray-600' : 'text-orange-100'} hover:text-orange-500 transition-colors duration-200 text-sm font-medium px-3 py-2 rounded-md bg-transparent hover:bg-gray-100`}
                >
                  <span className="font-semibold text-xs">{displayLanguage.label}</span>
                  <span className="hidden lg:inline">{displayLanguage.name}</span>
                  <Icon name="ChevronDown" size={16} className={`transition-transform duration-200 ${isLanguageMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-lang-menu="true">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-150 flex items-center space-x-2 ${
                          i18n.language === lang.code ? 'bg-orange-50 text-orange-600 font-medium' : 'text-gray-500'
                        }`}
                      >
                        <span className="font-semibold text-xs w-8">{lang.label}</span>
                        <span>{lang.name}</span>
                        {i18n.language === lang.code && (
                          <Icon name="Check" size={16} className="ml-auto text-orange-500" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Language Selector */}
            <div className="md:hidden flex items-center">
              {/* Language Selector for Mobile */}
              <div className="relative" data-lang-menu="true">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className={`p-2 rounded-md ${isScrolled ? 'text-gray-500' : 'text-orange-200'} hover:text-orange-500 transition-colors duration-200 text-sm font-medium`}
                >
                  <span className="font-semibold">{displayLanguage.label}</span>
                </button>

                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-lang-menu="true">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-150 flex items-center space-x-2 ${
                          i18n.language === lang.code ? 'bg-orange-50 text-orange-600 font-medium' : 'text-gray-500'
                        }`}
                      >
                        <span className="font-semibold text-xs w-8">{lang.label}</span>
                        <span>{lang.name}</span>
                        {i18n.language === lang.code && (
                          <Icon name="Check" size={16} className="ml-auto text-orange-500" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;